import React from "react";
import { css, styled } from "goober";

import router from "@/__main__/router.mjs";

export const Header = styled("header", React.forwardRef)`
  --img-radius: var(--br);
  display: flex;
  align-items: center;
  width: 100%;
  max-width: var(--sp-container);
  margin-inline: auto;
  transition: opacity var(--transition);
  will-change: opacity, transform;

  .container {
    display: flex;
    align-items: var(--alignment);
    gap: var(--gap);
  }

  .inner-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-8);

    @container (inline-size <= 800px) {
      display: grid;
      gap: var(--sp-4);
      justify-content: unset;
    }
  }

  &.scrolled-away {
    opacity: 0;
  }

  .header-image--outer {
    position: relative;
  }

  .header-image--inner,
  .header-image--img,
  .header-icon--inner {
    border-radius: var(--img-radius);
  }
  .header-icon--inner,
  .header-image--inner {
    position: relative;
    height: var(--header-img-size, var(--sp-18));
    width: var(--header-img-size, var(--sp-18));
    overflow: hidden;

    @media (width <= 768px) {
      height: var(--sp-12);
      width: var(--sp-12);
    }
  }
  .header-image--inner {
    display: flex;
    align-items: center;
    justify-content: center;

    & > img {
      height: 100%;
      width: auto;
    }

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: var(--img-radius);
      box-shadow: inset 0 0 0 2px var(--border-color, var(--shade1-15));
    }

    &:is(a) {
      &:hover {
        border-color: var(--shade4);
      }
    }
  }
  .header-image--img {
    width: 100%;
    transform: scale(1.2);
  }

  .header-icon--inner {
    display: grid;
    place-content: center;
    aspect-ratio: 1;
    background: var(--shade7);
    box-shadow: inset 0px 2px 2px var(--shade3-25);

    svg {
      width: var(--sp-9);
      height: auto;
    }
  }

  .accent-pill,
  .accent-icon {
    position: absolute;
    bottom: 0;
    left: 50%;
  }

  .accent-pill {
    padding: var(--sp-0_5) var(--sp-2);
    background: var(--app-bg);
    border-radius: var(--sp-10);
    transform: translate(-50%, 50%);
  }
  .accent-icon {
    transform: translate(-50%, 50%);

    svg {
      width: var(--sp-8);
      height: auto;
    }
  }

  .type-page-header {
    gap: var(--sp-3);
  }

  .interpunct::before {
    content: "·";
  }

  transition: opacity var(--transition);

  /* &.empty-links {
    margin-bottom: var(--sp-2);
  } */
`;

export const ComplexLink = styled("div")`
  display: flex;
  align-items: center;

  svg:not(.current) {
    filter: grayscale(100%);
  }
`;

export const Links = styled("nav")`
  position: sticky;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  > ul {
    display: flex;
    align-items: center;
    gap: var(--sp-1) var(--sp-4);
    flex-wrap: wrap;
    border-bottom: 1px solid var(--shade6);

    > li {
      display: inline-block;
    }
  }

  a,
  button {
    display: flex;
    align-items: center;
    position: relative;
    height: var(--sp-10);
    padding: 0 var(--sp-3);
    color: var(--shade2);
    transition: color var(--transition);
    background: transparent;
    cursor: pointer;

    &:has(.new-badge) {
      padding-inline-end: var(--sp-6);
    }

    &:hover {
      color: var(--shade0);
    }

    &::before {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      width: var(--sp-6);
      height: 3px;
      background: var(--game-accent-color);
      border-top-left-radius: var(--br-sm);
      border-top-right-radius: var(--br-sm);
      transform: translate(-50%, 5px);
      opacity: 0;
      visibility: hidden;
      transition:
        opacity var(--transition),
        visibility var(--transition),
        transform var(--transition);
    }

    &.current {
      color: var(--shade0);

      &::before {
        opacity: 1;
        visibility: visible;
        transform: translate(-50%, 0);
      }
    }

    &.disabled {
      cursor: default;
      pointer-events: none;
    }
  }

  .new-badge {
    --color: var(--orange);
    position: absolute;
    top: 0;
    right: 0;
    color: var(--color);
    line-height: 1;
    border-radius: var(--br-sm);
    translate: 25% 30%;
    text-shadow: 0 0 15px var(--color);
  }
`;

export const dropdownArrow = () => css`
  --arrow-size: 5px;

  &::after {
    content: "";
    width: 0;
    height: 0;
    margin-left: var(--sp-2);
    border-left: var(--arrow-size) solid transparent;
    border-right: var(--arrow-size) solid transparent;

    border-top: var(--arrow-size) solid var(--shade2);
  }

  .current & {
    &::after {
      border-top: var(--arrow-size) solid var(--shade0);
    }
  }
`;

export const dropdownContainerCss = () => css`
  position: relative;

  & .dropdown {
    display: none;
    position: absolute;
    width: max-content;
    top: calc(100% - var(--sp-1));
    transform: translateX(calc(-1 * var(--sp-4)));
    background: var(--shade7);
    border-radius: var(--br-sm);
    box-shadow:
      0px 3px 3px -35px rgba(0, 0, 0, 0.12),
      0px 6px 8px -35px rgba(0, 0, 0, 0.27),
      0px 10px 15px -35px rgba(0, 0, 0, 0.33),
      0px 12px 25px -35px rgba(0, 0, 0, 0.39),
      0px 20px 41px -35px rgba(0, 0, 0, 0.45),
      0px 40px 72px -35px rgba(0, 0, 0, 0.54),
      0px 80px 156px -35px rgba(0, 0, 0, 0.73);
  }
  & .dropdown.visible {
    display: block;
  }
`;

export const linksWithMenus = () => css`
  z-index: 5;
`;

export const LinksLoading = styled("li")`
  width: 100%;
  overflow: hidden;
  height: var(--sp-10);
  background-color: var(--shade7);
  &::before {
    content: ".";
    color: transparent;
  }
`;

export const PageDescription = styled("p")`
  width: 100%;
  margin-block: var(--sp-1);

  &[stuck] {
    opacity: 0;
  }
`;

export const cssTitleTransition = (isBackwards = router.isBackwards) => css`
  transition: var(--transition);
  &.enter-from {
    opacity: 0;
    transform: translateX(${isBackwards ? 20 : -20}px);
  }
  &.leave-to {
    opacity: 0;
    transform: translateX(${isBackwards ? -20 : 20}px);
  }
  &.leave {
    position: absolute;
  }
`;
